import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Badge,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Separator,
  Input,
  cn,
} from "lift-ui";
import { Filter as FilterIcon, ChevronDown, X, Search } from "lucide-react";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterSection {
  /** Stable id for the section, e.g. "user" */
  key: string;
  /** Display label, e.g. "User" */
  label: string;
  /** All available options for this section */
  options: FilterOption[];
  /** Selected values (controlled by parent) */
  value: string[];
  /** Setter — called with the new selection on Apply */
  onChange: (next: string[]) => void;
  /** Whether the section is interactive */
  disabled?: boolean;
  /** Optional tooltip / hint when disabled */
  disabledHint?: string;
  /** When true, render a search box above the options list (useful for
   *  high-cardinality sections like "User"). */
  searchable?: boolean;
  /** Placeholder for the per-section search box (defaults to "Search…"). */
  searchPlaceholder?: string;
}

interface Props {
  sections: FilterSection[];
  /** Optional align for the popover (defaults to "start") */
  align?: "start" | "center" | "end";
}

/**
 * Unified popover-based filter UI shared by the Sessions, Workflow Runs,
 * and Master Memories admin panels.
 *
 * Layout (top → bottom, all inside a single PopoverContent that flex-
 * columns with a capped max-height):
 *   ┌─ Header (title · Clear All · X)              [flex-shrink-0]
 *   │  Separator
 *   │  Attached chips (DRAFT state)                [flex-shrink-0]
 *   │  Sections (Collapsible × N)                  [flex-1, scrolls]
 *   │  Separator
 *   └─ Apply Filters footer                        [flex-shrink-0]
 *
 * The middle "sections" area is the only thing that scrolls so the
 * header, chips and Apply button stay visible no matter how many
 * filters or how many options are active.
 */
export const FiltersPopover: React.FC<Props> = ({
  sections,
  align = "start",
}) => {
  const [open, setOpen] = useState(false);

  // Draft state mirrors the committed values until the user clicks Apply.
  const [draft, setDraft] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(sections.map((s) => [s.key, s.value]))
  );

  // Per-section search query (only relevant for sections with
  // `searchable: true`). Keyed by section.key.
  const [search, setSearch] = useState<Record<string, string>>({});

  // Track which sections are expanded inside the popover. Default to the
  // first section so the popover never opens "all collapsed" — the user
  // immediately sees something to interact with.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(sections.map((s, i) => [s.key, i === 0]))
  );

  // When the popover opens, sync the draft with whatever is currently
  // applied so the user sees the actual current state — not stale draft
  // values from a previous unsubmitted edit. Also clear any per-section
  // search so the next session starts fresh.
  useEffect(() => {
    if (open) {
      setDraft(Object.fromEntries(sections.map((s) => [s.key, s.value])));
      setSearch({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Committed (post-Apply) selection count — drives the trigger badge. */
  const totalActive = useMemo(
    () => sections.reduce((sum, s) => sum + s.value.length, 0),
    [sections]
  );

  /** Draft (in-progress) selection count — drives the chips inside the
   *  popover so they update live as the user toggles checkboxes. */
  const draftActive = useMemo(
    () =>
      sections.reduce((sum, s) => sum + (draft[s.key]?.length || 0), 0),
    [draft, sections]
  );

  const handleSectionToggle = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOptionToggle = (
    sectionKey: string,
    value: string,
    checked: boolean
  ) => {
    setDraft((prev) => {
      const current = prev[sectionKey] || [];
      const next = checked
        ? Array.from(new Set([...current, value]))
        : current.filter((v) => v !== value);
      return { ...prev, [sectionKey]: next };
    });
  };

  const handleSelectAll = (
    sectionKey: string,
    visibleOptions: FilterOption[],
    allSelected: boolean
  ) => {
    setDraft((prev) => {
      const current = prev[sectionKey] || [];
      if (allSelected) {
        // Unselect all currently visible options (preserve any selections
        // hidden by the section search).
        const visibleValues = new Set(visibleOptions.map((o) => o.value));
        return {
          ...prev,
          [sectionKey]: current.filter((v) => !visibleValues.has(v)),
        };
      }
      // Select all currently visible options on top of existing selection.
      const merged = new Set([...current, ...visibleOptions.map((o) => o.value)]);
      return { ...prev, [sectionKey]: Array.from(merged) };
    });
  };

  const handleApply = () => {
    sections.forEach((s) => {
      const next = draft[s.key] || [];
      const same =
        next.length === s.value.length &&
        next.every((v) => s.value.includes(v));
      if (!same) s.onChange(next);
    });
    setOpen(false);
  };

  const handleClearAll = () => {
    setDraft(Object.fromEntries(sections.map((s) => [s.key, []])));
  };

  const handleRemoveDraftChip = (sectionKey: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [sectionKey]: (prev[sectionKey] || []).filter((v) => v !== value),
    }));
  };

  const getOptionLabel = (sectionKey: string, value: string): string => {
    const section = sections.find((s) => s.key === sectionKey);
    if (!section) return value;
    return section.options.find((o) => o.value === value)?.label ?? value;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className={cn(
            "h-[46px] gap-2 bg-white border-input text-[#242424] hover:border-[#585755]",
            "px-3"
          )}
          aria-label="Open filters"
        >
          <FilterIcon className="h-4 w-4" />
          <span>Filters</span>
          {totalActive > 0 && (
            <Badge
              variant="secondary"
              className="ml-1 h-5 min-w-[20px] px-1.5 rounded-full text-xs bg-[#E3FF6A] text-[#242424] border-0"
            >
              {totalActive}
            </Badge>
          )}
          <ChevronDown
            className={cn(
              "h-4 w-4 text-[#585755] transition-transform",
              open && "rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>

      {/* Popover container: flex column with a viewport-capped max-height
       *  so very large filter sets always show the Apply button. The inner
       *  sections area is the only scrollable region. */}
      <PopoverContent
        align={align}
        sideOffset={8}
        collisionPadding={16}
        className={cn(
          "w-[380px] p-0 rounded-[12px] border border-[#E5E1DF] bg-white shadow-lg",
          // Cap height to whichever is smaller: ~65% of viewport, the
          // radix-computed available space, or 520px. Without the
          // `--radix-popover-content-available-height` clamp, the popover
          // can render taller than the space between the trigger and the
          // bottom of the screen and the Apply Filters footer gets cut
          // off — observed at the Sessions panel where the trigger sits
          // ~150px from the top so available height is ~viewport-220px.
          "flex flex-col",
          "max-h-[min(65vh,520px,var(--radix-popover-content-available-height))]"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
          <span className="text-base font-semibold text-[#242424]">
            Filters
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm font-medium text-[#D90B0B] hover:underline focus:outline-none"
            >
              Clear All
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[#757271] hover:text-[#242424] focus:outline-none"
              aria-label="Close filters"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <Separator className="bg-[#E5E1DF] flex-shrink-0" />

        {/* Attached filter chips — DRAFT state. Capped to its own scroll
         *  area so a wall of chips can't push the sections / Apply button
         *  out of view. */}
        {draftActive > 0 && (
          <div className="px-4 pt-3 pb-2 flex-shrink-0 max-h-[120px] overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {sections.flatMap((s) =>
                (draft[s.key] || []).map((v) => (
                  <span
                    key={`${s.key}:${v}`}
                    className={cn(
                      "inline-flex items-center gap-1.5 h-8 px-3 rounded-[8px]",
                      "bg-white border border-[#E5E1DF] text-sm text-[#242424]",
                      "hover:border-[#A09D9B] transition-colors"
                    )}
                  >
                    <span>
                      <span className="text-[#242424]">{s.label}: </span>
                      <span className="text-[#242424]">
                        {getOptionLabel(s.key, v)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDraftChip(s.key, v)}
                      className="ml-0.5 text-[#757271] hover:text-[#242424] focus:outline-none"
                      aria-label={`Remove ${s.label} ${getOptionLabel(s.key, v)}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>
        )}

        {/* Sections — the only flex-1 region, so this is what actually
         *  scrolls when content overflows. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {sections.map((section, idx) => {
            const draftSelected = draft[section.key] || [];
            const sectionSearch = search[section.key] || "";
            const visibleOptions = section.searchable && sectionSearch.trim()
              ? section.options.filter((o) =>
                  o.label.toLowerCase().includes(sectionSearch.trim().toLowerCase())
                )
              : section.options;
            const allVisibleSelected =
              visibleOptions.length > 0 &&
              visibleOptions.every((o) => draftSelected.includes(o.value));
            const isOpen = !!expanded[section.key];
            return (
              <React.Fragment key={section.key}>
                {idx > 0 && <Separator className="bg-[#F1EEEB]" />}
                <Collapsible open={isOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      onClick={() => handleSectionToggle(section.key)}
                      disabled={section.disabled}
                      title={
                        section.disabled ? section.disabledHint : undefined
                      }
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 text-left",
                        "hover:bg-[#F8FAFC] transition-colors",
                        section.disabled && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-[#242424]">
                        {section.label}
                        {draftSelected.length > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E5E1DF] text-xs text-[#242424]">
                            {draftSelected.length}
                          </span>
                        )}
                      </span>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 text-[#757271] transition-transform",
                          isOpen && "rotate-180"
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-4 pb-3">
                    {/* Per-section search (only when section.searchable) */}
                    {section.searchable && (
                      <div className="relative mb-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#757271] pointer-events-none" />
                        <Input
                          value={sectionSearch}
                          onChange={(e) =>
                            setSearch((prev) => ({
                              ...prev,
                              [section.key]: e.target.value,
                            }))
                          }
                          placeholder={
                            section.searchPlaceholder ||
                            `Search ${section.label.toLowerCase()}…`
                          }
                          className="pl-8 h-9 text-sm bg-white border-input"
                          aria-label={`Search ${section.label}`}
                        />
                      </div>
                    )}

                    {visibleOptions.length === 0 ? (
                      <div className="text-xs text-[#757271] py-2 px-2">
                        {sectionSearch
                          ? "No matching options"
                          : "No options available"}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {/* Select All operates on currently *visible* options,
                         *  so when a section search is active, "Select All"
                         *  selects only the matching subset (and preserves
                         *  selections hidden by the search). */}
                        <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F8FAFC] cursor-pointer select-none">
                          <Checkbox
                            checked={allVisibleSelected}
                            onCheckedChange={() =>
                              handleSelectAll(
                                section.key,
                                visibleOptions,
                                allVisibleSelected
                              )
                            }
                            disabled={section.disabled}
                            className="h-4 w-4"
                          />
                          <span className="text-sm text-[#242424]">
                            Select All ({visibleOptions.length})
                          </span>
                        </label>
                        <div className="max-h-[180px] overflow-y-auto pr-1">
                          {visibleOptions.map((opt) => {
                            const checked = draftSelected.includes(opt.value);
                            return (
                              <label
                                key={opt.value}
                                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F8FAFC] cursor-pointer select-none"
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) =>
                                    handleOptionToggle(
                                      section.key,
                                      opt.value,
                                      !!v
                                    )
                                  }
                                  disabled={section.disabled}
                                  className="h-4 w-4"
                                />
                                <span
                                  className="text-sm text-[#242424] truncate"
                                  title={opt.label}
                                >
                                  {opt.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </React.Fragment>
            );
          })}
        </div>

        <Separator className="bg-[#E5E1DF] flex-shrink-0" />

        {/* Apply Filters — pinned at the bottom, never scrolls away. */}
        <div className="px-4 py-3 flex-shrink-0">
          <Button
            type="button"
            onClick={handleApply}
            className="w-full h-10 bg-[#E3FF6A] hover:bg-[#DAF565] text-[#242424] border border-[#DAF565]"
          >
            Apply Filters
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** Backward-compat alias — older imports referenced `SessionFiltersPopover`
 *  before the component was generalised across panels. New code should
 *  import `FiltersPopover` directly. */
export const SessionFiltersPopover = FiltersPopover;

export default FiltersPopover;
